import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Form, Input, Select, Checkbox, Empty, Card } from "antd";
import { Error, Success } from "../../../commons/Messages/Messages";
import { modulesArrayForPermissions } from "../../../commons/modulesForPerm";
import { isAuthenticated } from "../../../commons/Auth/auth";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import UpdateRole from "./UpdateRole";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import GeneralModal from "../General.modal";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import PageToolbar from "../../../design-system/PageToolbar";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./RolesManagement.css";

const RoleManagement = () => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleObject, setSelectedRoleObject] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/roles/all`,
        { serviceProviderId: isAuthenticated()?.serviceProviderId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setRoles(res.data.data);
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/employees/limited`,
        { pageSize: 100000, pageNo: 0 },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setEmployees(
        res.data.data?.map(emp => ({
          value: emp?._id,
          label: emp?.firstName + " " + emp?.lastName
        }))
      );
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const handleRoleSelect = roleId => {
    const role = roles.find(r => r._id === roleId);
    setSelectedRoleObject(role);
    setSelectedRole(roleId);

    const permissionsObject = {};
    if (role?.permissions?.length > 0) {
      const permissionData = role.permissions[0];
      Object.keys(permissionData).forEach(moduleName => {
        permissionsObject[moduleName] = permissionData[moduleName];
      });
    }

    setPermissions(permissionsObject);
  };

  const handlePermissionChange = (module, checkedValues) => {
    setPermissions(prev => ({
      ...prev,
      [module]: checkedValues
    }));
  };

  const handleSavePermissions = async () => {
    try {
      await axios
        .put(
          `${process.env.REACT_APP_BACKEND_URL}/roles/permissions`,
          { roleId: selectedRole, permissions },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        )
        .then(res => {
          if (res.status === 200) {
            Success(t("Permissions updated successfully!"));
            fetchRoles();
          } else {
            Error(res.data.errorMessage);
          }
        });
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const handleAddRole = async values => {
    try {
      await axios
        .post(
          `${process.env.REACT_APP_BACKEND_URL}/roles/create`,
          {
            serviceProviderId: isAuthenticated()?.serviceProviderId,
            title: values?.title,
            employees: values?.employees
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        )
        .then(res => {
          if (res.status === 200) {
            Success(t("Role added successfully!"));
            fetchRoles();
            form.resetFields();
          } else {
            Error(res.data.errorMessage);
          }
        });
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchEmployees();
  }, []);

  const handleDeleteRole = async () => {
    try {
      await axios
        .delete(`${process.env.REACT_APP_BACKEND_URL}/roles/delete/${selectedRole}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(res => {
          if (res.status === 200) {
            Success(t("Role deleted successfully!"));
            fetchRoles();
          } else {
            Error(res.data.errorMessage);
          }
        });
    } catch (err) {
      generalErrorAlert(err);
    }
  };
  const plainOptions = ["Create", "Read", "Update", "Delete"];
  // Some modules only have a subset of the actions (Dashboard: Read, Attendance-corrections: Update)
  const optionsOf = name =>
    modulesArrayForPermissions.find(module => module.name === name)?.permissions || plainOptions;
  const onCheckAllChange = e => {
    if (e.target.checked === true) {
      modulesArrayForPermissions.map(module =>
        setPermissions(prev => ({
          ...prev,
          [module.name]: optionsOf(module.name)
        }))
      );
    } else {
      modulesArrayForPermissions.map(module =>
        setPermissions(prev => ({
          ...prev,
          [module.name]: []
        }))
      );
    }
  };

  const onCheckTheRow = (e, moduleName) => {
    if (e.target.checked === true) {
      setPermissions(prev => ({
        ...prev,
        [moduleName]: optionsOf(moduleName)
      }));
    } else {
      setPermissions(prev => ({
        ...prev,
        [moduleName]: []
      }));
    }
  };

  const items = (
    <>
      <Form form={form} onFinish={handleAddRole} layout="vertical">
        <Form.Item
          name="title"
          label={t("Title")}
          rules={[{ required: true, message: t("enter", { field: t("Title") }) }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="employees" label={t("Assign Employees")}>
          <Select mode="multiple" options={employees} />
        </Form.Item>
        {/* <Button type="primary" htmlType="submit">
                        {t("Submit")}
                    </Button> */}
      </Form>
    </>
  );

  const optionsFor = module =>
    module?.permissions?.length > 0 ? module.permissions : plainOptions;
  const rowState = module => {
    const granted = permissions[module.name] || [];
    const all = optionsFor(module).every(option => granted.includes(option));
    return { granted, all, some: granted.length > 0 && !all };
  };
  const allGranted = modulesArrayForPermissions.every(module => rowState(module).all);
  const someGranted =
    !allGranted && modulesArrayForPermissions.some(module => rowState(module).granted.length > 0);

  const rowCheckbox = module => {
    const { all, some } = rowState(module);
    return (
      <Checkbox checked={all} indeterminate={some} onChange={e => onCheckTheRow(e, module.name)}>
        {t("All")}
      </Checkbox>
    );
  };

  const permissionGroup = module => (
    <Checkbox.Group
      className="roles-page__perm-group"
      options={optionsFor(module).map(option => ({ label: t(option), value: option }))}
      value={permissions[module.name] || []}
      onChange={checkedValues => handlePermissionChange(module.name, checkedValues)}
    />
  );

  const grantAll = (
    <Checkbox checked={allGranted} indeterminate={someGranted} onChange={onCheckAllChange}>
      {t("Grant all permissions")}
    </Checkbox>
  );

  return (
    <div className="roles-page">
      <h2 className="roles-page__title">{t("Role Management")}</h2>
      <PageToolbar
        filters={
          <Select
            placeholder={t("Select Role")}
            aria-label={t("Select Role")}
            onChange={handleRoleSelect}
            options={roles.map(role => ({ value: role._id, label: role.title }))}
          />
        }
        actions={
          <>
            {selectedRole && (
              <>
                <UpdateRole
                  updateParent={fetchRoles}
                  data={selectedRoleObject}
                  employees={employees}
                />
                <DeleteModal
                  disabled={
                    (selectedRoleObject?.isDefault && "Default") ||
                    (selectedRoleObject?.employees?.length > 0 && "Employees present")
                  }
                  text="Role"
                  onClick={handleDeleteRole}
                />
              </>
            )}
            <MainButtonComp text="Add Role" addBtn onClick={showModal} />
          </>
        }
      />
      {selectedRole === null ? (
        <Empty />
      ) : (
        <div className="roles-page__matrix">
          {isMobile ? (
            // Phones: one card per module with its permission checkboxes.
            <>
              <div className="roles-page__grant-all">{grantAll}</div>
              <ul className="roles-page__cards">
                {modulesArrayForPermissions.map(module => (
                  <li key={module.name}>
                    <Card
                      size="small"
                      title={t(module.name)}
                      extra={rowCheckbox(module)}
                      className="roles-page__card"
                    >
                      {permissionGroup(module)}
                    </Card>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <ResponsiveTable
              mobileLayout="table"
              dataSource={modulesArrayForPermissions.map(module => ({
                key: module.name,
                module: t(module.name),
                permissions: (
                  <div className="roles-page__perm-row">
                    {rowCheckbox(module)}
                    {permissionGroup(module)}
                  </div>
                )
              }))}
              columns={[
                { title: t("Module"), dataIndex: "module" },
                { title: grantAll, dataIndex: "permissions" }
              ]}
              pagination={false}
            />
          )}
          <div className="roles-page__save">
            <Button type="primary" onClick={handleSavePermissions}>
              {t("Save")}
            </Button>
          </div>
        </div>
      )}
      <GeneralModal
        title={t("Create Role")}
        onOk={() => form.submit()}
        open={isModalOpen}
        okText={t("Save")}
        onCancel={handleCancel}
        items={items}
      />
    </div>
  );
};

export default RoleManagement;
